/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ValidatorFn, Validators} from '@angular/forms';
import {InputTextComponent} from '../../../../shared/input/input-text/input-text.component';
import {InputListModel} from '../input-list.model';
import {InputTypeEnum} from '../../../../shared/input/input-type.enum';
import {BusinessSettingsModal} from '../business-settings-modal';
import {BusinessSettingsModalState} from '../business-settings-modal.state';
import {TypeaheadHelperService} from '../../../../shared/services/typeahead-helper.service';
import {InputMessagesService} from '../../../../shared/input/service/input-messages.service';
import {WarningsEnum} from '../../../../shared/warnings/warnings.enum';
import {EntityElementModel} from '../../../../shared/entity-tree/entity-element/models/entity-element.model';
import {take} from 'rxjs/operators';
import {timer} from 'rxjs';
import {MandatoryMessageTypeEnum} from '../../../../shared/input/service/mandatory-message-type.enum';
import {BusinessCategoryModel} from '../../../../api/ms-settings/business/schema/v1/contracts/pool/business-category.model';

@Component({
    selector: 'app-bi-category-modal',
    templateUrl: './business-category-modal.component.html',
    styleUrls: ['../business-settings-modal.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BusinessCategoryModalComponent extends BusinessSettingsModal<BusinessSettingsModalState<BusinessCategoryModel>> {

    @ViewChild('biCategoryName') nameInput: InputTextComponent;
    @ViewChild('biCategoryCode') codeInput: InputTextComponent;
    @ViewChild('biCategoryRange') rangeInput: InputTextComponent;

    readonly ENTITY_NAME = 'BI category';
    readonly EDIT_TITLE = 'Edit BI category';
    readonly ADD_TITLE = 'Add a BI category';
    readonly EDIT_SUBMIT_TITLE = 'Edit';
    readonly ADD_SUBMIT_TITLE = 'Add';

    validators: { [key: string]: ValidatorFn[] } = {
        name: [Validators.pattern(/^[\w-/\s\\.(),]{1,15}$/)],
        code: [Validators.pattern(/^[\w-/\s\\.(),*]{1,4}$/)],
        range: [Validators.pattern(/^[\w-/\s\\.(),>=<]{1,15}$/)]
    };

    public nameMessages = new Map<WarningsEnum, string[]>();
    public codeMessages = new Map<WarningsEnum, string[]>();
    public rangeMessages = new Map<WarningsEnum, string[]>();

    constructor(public typeahead: TypeaheadHelperService, inputMessages: InputMessagesService) {
        super(inputMessages);
    }

    setEntity(element: EntityElementModel<BusinessCategoryModel>): void {
        this.storeState({
            originalElement: element,
            originalValue: element.originalValue,
            currentValue: element.currentValue
        });
        this.onReady.pipe(take(1))
.subscribe(() => {
            timer(0)
.subscribe(() => {
                this.nameInput.setValue(this.state.currentValue.name);
                this.codeInput.setValue(this.state.currentValue.code);
                this.rangeInput.setValue(this.state.currentValue.range);
                this.setActiveInputState(element.active, true);
            });
        });
    }

    updateStoreStateName = (name: string, message: Map<WarningsEnum, string[]>): void => {
        this.nameMessages = new Map(message);
        const errors = message.get(WarningsEnum.ERROR);
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                name: name
            },
            invalid: {
                ...this.state.invalid,
                name: (name == null || name.length === 0) || (errors != null && errors.length > 0)
            }
        });
    };
    updateStoreStateCode = (code: string, message: Map<WarningsEnum, string[]>): void => {
        this.codeMessages = new Map(message);
        const errors = message.get(WarningsEnum.ERROR);
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                code: code
            },
            invalid: {
                ...this.state.invalid,
                code: (code == null || code.length === 0) || (errors != null && errors.length > 0)
            }
        });
    };
    updateStoreStateRange = (range: string, message: Map<WarningsEnum, string[]>): void => {
        this.rangeMessages = new Map(message);
        const errors = message.get(WarningsEnum.ERROR);
        this.storeState({
            currentValue: {
                ...this.state.currentValue,
                range: range
            },
            invalid: {
                ...this.state.invalid,
                range: (range == null || range.length === 0) || (errors != null && errors.length > 0)
            }

        });
    };

    onInputValueChange(value: string, message: Map<WarningsEnum, string[]>, viewChildInputControl: InputTextComponent,
                       callbackFunction: (value: string, errors: Map<WarningsEnum, string[]>) => void = (): void => {
                       },
                       validateIfCodeAlreadyExists?: boolean, entity?: string): void {
        this.inputMessages.mandatoryWarnings(viewChildInputControl.inputControl, message, MandatoryMessageTypeEnum.SAVE);
        this.inputMessages.invalidInputValueError(
                viewChildInputControl.inputControl,
                message,
                viewChildInputControl.label);
        if (validateIfCodeAlreadyExists) {
            const errors = message.get(WarningsEnum.ERROR);
            if (errors.length === 0) {
                this.manageAlreadyExistsCode(value, entity, message, viewChildInputControl);
            }
        }
        callbackFunction(value, message);

        this.setModalValidity();
    }

    protected generateInputs(): InputListModel[] {
        return [{
            input: this.nameInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.codeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }, {
            input: this.rangeInput,
            type: InputTypeEnum.TEXT,
            mandatory: true
        }];
    }
}
